/**
 * Composable for membership-aware action gating.
 *
 * Provides computed refs and utilities to check if the current user
 * has valid membership for the active site (EAUN or EAU).
 *
 * Member status codes:
 * - 'a' = Active (grants access)
 * - 'c' = Cancelled
 * - 'i' = Inactive
 * - 's' = Stopped
 * - 'n' = New
 *
 * @example
 * ```vue
 * <script setup>
 * const { canAccessMemberContent, isAuthenticated, promptLogin, gateReason } = useMembershipGate();
 * </script>
 *
 * <template>
 *   <BaseButton v-if="canAccessMemberContent" @click="doAction">Access</BaseButton>
 *   <BaseButton v-else-if="!isAuthenticated" @click="promptLogin">Log in</BaseButton>
 *   <p v-else>Requires {{ requiredMembershipCode }} membership</p>
 * </template>
 * ```
 */

export type GateReason = 'not-logged-in' | 'wrong-membership' | 'inactive-membership' | null;

export interface MembershipGateReturn {
    /** Current site group ('eaun' | 'uroweb' | other) */
    siteGroup: ComputedRef<string | undefined>;
    /** Required membership code for current site ('EAUN' | 'EAU') */
    requiredMembershipCode: ComputedRef<string>;
    /** Whether the user is authenticated */
    isAuthenticated: ComputedRef<boolean>;
    user: ComputedRef<any>;
    /** Whether user has valid membership for current site */
    hasValidMembership: ComputedRef<boolean>;
    /** Whether user can access member-only content (authenticated + valid membership) */
    canAccessMemberContent: ComputedRef<boolean>;
    /** Reason why access is denied, or null if access is granted */
    gateReason: ComputedRef<GateReason>;
    promptLogin: () => Promise<void>;
}

export const useMembershipGate = (): MembershipGateReturn => {
    const { data: session, status, signIn } = useAuth();
    const { activeSite } = useMultisite();
    const route = useRoute();

    const siteGroup = computed(() => activeSite.value?.group);

    const requiredMembershipCode = computed(() => {
        // EAUN site requires EAUN membership, EAU (uroweb) site requires EAU membership
        return siteGroup.value === 'eaun' ? 'EAUN' : 'EAU';
    });

    const isAuthenticated = computed(() => status.value === 'authenticated');

    const user = computed(() => session.value?.user);

    const hasValidMembership = computed(() => {
        // const memberStatus = user.value?.memberStatus;
        const membershipCode = user.value?.membershipCode;

        // All memberships may see content for the EAU site, but only active EAUN members may see content for the EAUN site
        if (requiredMembershipCode.value === 'EAU') {
            return true; // Allow all authenticated users to access EAU content regardless of membership status
        }

        // Must be active ('a') and have matching membership code
        // For now we're lenient and ignore memberStatus
        // return memberStatus === 'a' && membershipCode === requiredMembershipCode.value;
        return membershipCode === requiredMembershipCode.value;
    });

    const canAccessMemberContent = computed(() => {
        return isAuthenticated.value && hasValidMembership.value;
    });

    const gateReason = computed<GateReason>(() => {
        if (!isAuthenticated.value) {
            return 'not-logged-in';
        }

        const memberStatus = user.value?.memberStatus;
        const membershipCode = user.value?.membershipCode;

        // Check if user is not active (anything other than 'a')
        if (memberStatus && memberStatus !== 'a') {
            return 'inactive-membership';
        }

        // Check if user has wrong membership type
        if (membershipCode && membershipCode !== requiredMembershipCode.value) {
            return 'wrong-membership';
        }

        // Check if user has no membership at all
        if (!memberStatus || !membershipCode) {
            return 'wrong-membership';
        }

        return null;
    });

    const promptLogin = async() => {
        await signIn('uroweb', { redirectTo: route.fullPath });
    };

    return {
        siteGroup,
        requiredMembershipCode,
        isAuthenticated,
        user,
        hasValidMembership,
        canAccessMemberContent,
        gateReason,
        promptLogin,
    };
};
